import React from "react";
import { graphql, createRefetchContainer, RelayRefetchProp } from "react-relay";
import { Disposable } from "react-relay";
import { createStyles, WithStyles, withStyles } from "@material-ui/core/styles";
import Typography from "@material-ui/core/Typography";
import List from "@material-ui/core/List";
import AppBar from "@material-ui/core/AppBar";
import Toolbar from "@material-ui/core/Toolbar";
import Button from "@material-ui/core/Button";
import Checkbox from "@material-ui/core/Checkbox";
import FormControlLabel from "@material-ui/core/FormControlLabel";

import LoadingScreen from "../common/LoadingScreen";
import LabelingIssueListItem from "./LabelingIssueListItem";

import { ListIssuesByViewer_viewer } from "../../__generated__/ListIssuesByViewer_viewer.graphql";

const styles = theme =>
  createStyles({
    grow: {
      flexGrow: 1
    },
    title: {
      display: "none",
      [theme.breakpoints.up("sm")]: {
        display: "block"
      }
    },
    appBar: {},
    wrapper: {
      border: "1px solid rgba(0, 0, 0, 0.23)",
      borderRadius: 4,
      backgroundColor: theme.palette.background.paper,
      margin: 32
    },
    toolbarRoot: {
      flexWrap: "wrap"
    },
    list: {
      display: "flex",
      flexDirection: "column",
      alignItems: "center",
      justifyContent: "flex-start"
    }
  });

interface Props extends WithStyles<typeof styles> {
  viewer: ListIssuesByViewer_viewer;
  relay: RelayRefetchProp;
}
type State = {
  showNeedsLabelerAttention: boolean;
  showNeedsReviewerAttention: boolean;
  showResolved: boolean;
  pendingRefetch: Disposable | null;
  isLoadingMore: boolean;
};

class ListIssuesByViewer extends React.Component<Props, State> {
  state = {
    showNeedsLabelerAttention: true,
    showNeedsReviewerAttention: true,
    showResolved: false,
    pendingRefetch: null,
    isLoadingMore: false
  };
  render() {
    const { viewer, classes } = this.props;
    const {
      pendingRefetch,
      isLoadingMore,
      showNeedsReviewerAttention,
      showNeedsLabelerAttention,
      showResolved
    } = this.state;

    const { endCursor } = this.props.viewer.issuesByViewer.pageInfo;
    return (
      <div className={classes.wrapper}>
        <AppBar color="default" position="static" className={classes.appBar}>
          <Toolbar classes={{ root: classes.toolbarRoot }}>
            <Typography className={classes.title} color="inherit" noWrap>
              Issues you created
            </Typography>
            <div className={classes.grow} />
            <div>
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={e => {
                      this.setState(
                        {
                          showNeedsReviewerAttention: e.currentTarget.checked
                        },
                        () => this._refetch()
                      );
                    }}
                    checked={showNeedsReviewerAttention}
                  />
                }
                label={"Needs your attention"}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={e => {
                      this.setState(
                        {
                          showNeedsLabelerAttention: e.currentTarget.checked
                        },
                        () => this._refetch()
                      );
                    }}
                    checked={showNeedsLabelerAttention}
                  />
                }
                label={"Needs assignee's attention"}
              />
              <FormControlLabel
                control={
                  <Checkbox
                    onChange={e => {
                      this.setState(
                        {
                          showResolved: e.currentTarget.checked
                        },
                        () => this._refetch()
                      );
                    }}
                    checked={showResolved}
                  />
                }
                label={"Is resolved"}
              />
            </div>
          </Toolbar>
        </AppBar>
        <List className={classes.list}>
          {viewer.issuesByViewer.edges.length === 0 && (
            <Typography>No issues match your description.</Typography>
          )}
          {/*If loading more, show loading icon on bottom*/}
          {pendingRefetch && !isLoadingMore && <LoadingScreen />}
          {viewer.issuesByViewer &&
            viewer.issuesByViewer.edges.map(({ node: issue }) => (
              <LabelingIssueListItem
                key={issue.id}
                issue={issue}
                viewer={viewer}
                sensorStream={issue}
              />
            ))}
          {isLoadingMore && <LoadingScreen />}
        </List>
        <Button
          fullWidth
          disabled={
            pendingRefetch !== null ||
            !viewer.issuesByViewer.pageInfo.hasNextPage
          }
          onClick={() => this._refetch(endCursor)}
        >
          Load More
        </Button>
      </div>
    );
  }

  _refetch = (after: string | null = null) => {
    const {
      pendingRefetch,
      showResolved,
      showNeedsLabelerAttention,
      showNeedsReviewerAttention
    } = this.state;
    if (pendingRefetch) {
      pendingRefetch.dispose();
    }
    const statuses = [];
    if (showResolved) {
      statuses.push("RESOLVED");
    }
    if (showNeedsLabelerAttention) {
      statuses.push("NEEDS_LABELER_ATTENTION");
    }
    if (showNeedsReviewerAttention) {
      statuses.push("NEEDS_QA_ATTENTION");
    }
    const refetchVariables = fragmentVariables => {
      const countIssuesByViewer = fragmentVariables.countIssuesByViewer;
      return {
        onlyByViewer: true,
        countIssuesByViewer,
        statuses,
        after
      };
    };
    this.setState({
      isLoadingMore: !!after,
      pendingRefetch: this.props.relay.refetch(
        refetchVariables,
        null, // Use the refetchVariables as renderVariables
        () => this.setState({ isLoadingMore: false, pendingRefetch: null }),
        { force: true } // Assuming we've configured a network layer cache, we want to ensure we fetch the latest data.
      )
    });
  };
}

export default withStyles(styles, { withTheme: true })(
  createRefetchContainer(
    ListIssuesByViewer,
    {
      viewer: graphql`
        fragment ListIssuesByViewer_viewer on Viewer
        @argumentDefinitions(
          countIssuesByViewer: { type: "Int", defaultValue: 15 }
          onlyByViewer: { type: "Boolean", defaultValue: true }
        ) {
          id
          issuesByViewer: issues(
            first: $countIssuesByViewer
            onlyByViewer: $onlyByViewer
            statuses: [NEEDS_LABELER_ATTENTION, NEEDS_QA_ATTENTION]
            after: null
          ) @connection(key: "ListIssuesByViewer_issuesByViewer", filters: []) {
            pageInfo {
              endCursor
              hasNextPage
            }
            edges {
              node {
                id
                ...LabelingIssueListItem_issue
                description
                frame {
                  id
                }
              }
            }
          }
        }
      `
    },
    graphql`
      query ListIssuesByViewerRefetchQuery(
        $statuses: [LabelingIssueStatus!]
        $onlyByViewer: Boolean
        $countIssuesByViewer: Int!
        $after: String
      ) {
        viewer {
          id
          issuesByViewer: issues(
            statuses: $statuses
            onlyByViewer: $onlyByViewer
            first: $countIssuesByViewer
            after: $after
          ) @connection(key: "ListIssuesByViewer_issuesByViewer", filters: []) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                ...LabelingIssueListItem_issue
                description
                frame {
                  id
                }
              }
            }
          }
        }
      }
    `
  )
);
